// @givmo/app-admin — Employees → INVITES tab (EP-WEB8; spec §97).
//
// The enrollment loop Givmo was missing: see how much of the company has joined, invite the rest, and
// hand the admin the announcement they will actually send.
//
// Contents: the enrollment-rate Meter · a status FilterBar whose chips carry real counts · the invite
// list with per-invite Resend · bulk add-by-email with a per-address result echo · the copy-to-clipboard
// announcement template.
//
// ── FOUR THINGS THIS SURFACE DELIBERATELY DOES NOT DO ────────────────────────────────────────────
//
// 1. NO REVOKE. `EmployeeInviteStatus` has a `REVOKED` state and the list renders it, but no route
//    withdraws an invite. A revoke button that 404s is worse than no button, so there is none — the
//    filter chip exists only to read history the backend may already contain.
//
// 2. NO "COPY SIGNUP LINK". There is no company-scoped enrollment URL to copy. Enrollment is
//    DOMAIN-driven: the employee installs the app, adds their work email, and verifies it by OTP; the
//    domain resolves them to this company. The announcement template therefore gives instructions plus
//    the real store links, and invents no URL.
//
// 3. NO CLAIM THAT AN EMAIL WENT OUT. The invite email is inert until its SendGrid template id is
//    provisioned in the environment, and the wire says so per-row via `sent_at`. A `null` renders
//    "Not emailed yet", never "Invited" — an admin waiting on a reply to an email that was never sent is
//    a failure this surface can prevent for free.
//
// 4. NO SERVER-SIDE STATUS FILTER. Filtering happens client-side over the complete, exhaustively-paged
//    set — the tab holds every row anyway, and a server round-trip per chip would buy nothing. The
//    CHIP COUNTS are a separate read: `GET /admin/company/invites/counts` (EP-BE12), server-computed
//    over all invites, so the summary row no longer depends on the list read at all. See
//    `use-invite-counts.ts` for why that read still keeps the old derivation as a fallback.
//
// Resend surfaces the server's own error text: the per-invite cooldown is a 400 with a real message, and
// paraphrasing it into "something went wrong" would hide the one fact the admin needs (that they already
// sent it, and when).

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  isGivmoError,
  sdk,
  type EmployeeInviteOut,
  type EmployeeInviteStatus,
} from '@givmo/api-client';
import { companiesGetCompanyEmployeeInviteCountsQueryKey } from '@givmo/api-client/query';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChip,
  Icon,
  Meter,
  Textarea,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { Check, Copy, MailPlus, Send } from 'lucide-react';
import { InlineError } from '../dashboard/sections/section-status';
import { MethodologyHint } from '../../components/methodology-hint';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../lib/constants';
import { useEmployerModulePaths } from '../../lib/module-paths';
import {
  announcementTemplate,
  canResendInvite,
  EMPTY_INVITE_COUNTS,
  enrollmentMethodology,
  enrollmentPercentLabel,
  fetchAllInvites,
  formatInviteDate,
  INVITE_FILTERS,
  inviteDeliveryNote,
  inviteFilterCount,
  INVITES_ALL_KEY,
  inviteStatusLabel,
  parseEmailInput,
  type EnrollmentRateView,
} from './invites-decode';
import { useInviteCounts } from './use-invite-counts';

const PREFIX = 'invites';

const STATUS_TONE: Record<EmployeeInviteStatus, 'success' | 'neutral' | 'warning'> = {
  ENROLLED: 'success',
  INVITED: 'warning',
  REVOKED: 'neutral',
};

export function InvitesTab({
  enrollment,
  enrollmentLoading,
  enrollmentIsError,
  onEnrollmentRetry,
  companyName,
  companyDomain,
}: {
  enrollment: EnrollmentRateView | null;
  enrollmentLoading: boolean;
  /**
   * The company read FAILED — a different state from "no headcount set", even though both leave
   * `enrollment` null. Telling an admin who already entered a headcount to go enter it again is
   * wrong advice, so the two are rendered differently.
   */
  enrollmentIsError: boolean;
  onEnrollmentRetry: () => void;
  companyName: string;
  companyDomain: string | null | undefined;
}) {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<EmployeeInviteStatus | null>(null);

  const list = useQuery({
    queryKey: INVITES_ALL_KEY,
    queryFn: ({ signal }) => fetchAllInvites(undefined, signal),
  });

  const invites = list.data ?? [];
  // Server-computed over ALL invites, independent of this list read — so a chip keeps its number even
  // when the rows fail to load. Zeroes while unknown, which is what the chips showed while loading before.
  const { counts: readCounts } = useInviteCounts();
  const counts = readCounts ?? EMPTY_INVITE_COUNTS;
  const visible = useMemo(
    () => (statusFilter === null ? invites : invites.filter((i) => i.status === statusFilter)),
    [invites, statusFilter],
  );

  // A write moves both the rows AND the totals, and they live in different cache entries now.
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: INVITES_ALL_KEY }),
      queryClient.invalidateQueries({
        queryKey: companiesGetCompanyEmployeeInviteCountsQueryKey(),
      }),
    ]);
  };

  // ── Resend one invite ──────────────────────────────────────────────────────────────────────────
  const [resendingId, setResendingId] = useState<string | null>(null);
  const resend = useMutation({
    mutationFn: (invite: EmployeeInviteOut) =>
      sdk.companiesResendCompanyEmployeeInvite({
        path: { invite_id: invite.id },
        throwOnError: true,
      }),
    onMutate: (invite) => setResendingId(invite.id),
    onSuccess: async () => {
      await refresh();
      toast.success('Invite re-sent');
    },
    onError: (error: unknown) => {
      // The cooldown 400 carries a real, specific message ("already sent, try again in N minutes").
      // Show it verbatim: a generic failure toast would hide the only actionable fact in it.
      toast.error(isGivmoError(error) ? error.message : 'Could not re-send the invite.');
    },
    onSettled: () => setResendingId(null),
  });

  // ── Bulk add by email ──────────────────────────────────────────────────────────────────────────
  const [emailInput, setEmailInput] = useState('');
  const parsedEmails = useMemo(() => parseEmailInput(emailInput), [emailInput]);

  const create = useMutation({
    mutationFn: (emails: string[]) =>
      sdk.companiesCreateCompanyEmployeeInvites({ body: { emails }, throwOnError: true }),
    onSuccess: async ({ data }) => {
      await refresh();
      setEmailInput('');
      // The three buckets are NOT errors — re-pasting a spreadsheet that overlaps the roster is the
      // normal case, and the request succeeded. Echo every bucket so the admin can reconcile the file
      // they pasted against what actually happened, address by address.
      const created = data?.created?.length ?? 0;
      const alreadyInvited = data?.already_invited ?? [];
      const alreadyEnrolled = data?.already_enrolled ?? [];
      const parts: string[] = [];
      if (created > 0) parts.push(`${created} invited`);
      if (alreadyInvited.length > 0) parts.push(`${alreadyInvited.length} already invited`);
      if (alreadyEnrolled.length > 0) parts.push(`${alreadyEnrolled.length} already enrolled`);
      toast.success(parts.length > 0 ? parts.join(' · ') : 'Nothing to invite');
      setLastResult({ alreadyInvited, alreadyEnrolled });
    },
    onError: (error: unknown) => {
      toast.error(
        isGivmoError(error) ? error.message : 'Could not send the invites. Please try again.',
      );
    },
  });

  /** The skip buckets from the last create, echoed per-address beneath the composer. */
  const [lastResult, setLastResult] = useState<{
    alreadyInvited: string[];
    alreadyEnrolled: string[];
  } | null>(null);

  // ── Columns ────────────────────────────────────────────────────────────────────────────────────
  // Memoized so rows skip re-render on unrelated parent state (composer keystrokes, the enrollment
  // card refetching). The deps are exactly what the cells close over.
  const columns: DataTableColumn<EmployeeInviteOut>[] = useMemo(
    () => [
      {
        id: 'email',
        header: 'Email',
        width: '40%',
        truncate: true,
        cell: (invite) => <span className="font-w600 text-text-strong">{invite.email}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        width: '120px',
        cell: (invite) => (
          <Badge tone={STATUS_TONE[invite.status] ?? 'neutral'} dot>
            {inviteStatusLabel(invite.status)}
          </Badge>
        ),
      },
      {
        id: 'delivery',
        header: 'Email',
        cell: (invite) => <span className="text-text-secondary">{inviteDeliveryNote(invite)}</span>,
      },
      {
        id: 'created',
        header: 'Added',
        width: '140px',
        cell: (invite) => (
          <span className="text-text-secondary">{formatInviteDate(invite.created_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        align: 'right',
        width: '110px',
        cell: (invite) =>
          // Only a pending invite can be re-sent; the server 400s the others, so no control is offered
          // for them at all rather than one that fails on click.
          canResendInvite(invite) ? (
            <div className="flex justify-end">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                loading={resendingId === invite.id}
                disabled={resend.isPending}
                leadingIcon={<Icon icon={Send} size={14} aria-hidden />}
                onClick={() => resend.mutate(invite)}
                testId={`${PREFIX}-resend-${invite.id}`}
              >
                Resend
              </Button>
            </div>
          ) : null,
      },
    ],
    [resendingId, resend.isPending, resend.mutate],
  );

  return (
    <div className="flex flex-col gap-24" data-testid={`${PREFIX}-tab`}>
      <EnrollmentCard
        enrollment={enrollment}
        loading={enrollmentLoading}
        isError={enrollmentIsError}
        onRetry={onEnrollmentRetry}
      />

      <AddInvitesCard
        value={emailInput}
        onChange={setEmailInput}
        parsedCount={parsedEmails.length}
        submitting={create.isPending}
        onSubmit={() => create.mutate(parsedEmails)}
        lastResult={lastResult}
      />

      <DataTable
        columns={columns}
        rows={visible}
        rowKey={(invite) => invite.id}
        rowTestId={(invite) => `${PREFIX}-row-${invite.id}`}
        loading={list.isPending}
        loadingTestId={`${PREFIX}-loading`}
        error={
          list.isError
            ? isGivmoError(list.error)
              ? list.error.message
              : 'Failed to load invites.'
            : undefined
        }
        errorTestId={`${PREFIX}-error`}
        retryTestId={`${PREFIX}-error-retry`}
        onRetry={() => void list.refetch()}
        toolbar={
          <FilterBar aria-label="Filter invites by status" testId={`${PREFIX}-filters`}>
            {INVITE_FILTERS.map((filter) => (
              <FilterChip
                key={filter.label}
                label={filter.label}
                // Counts come from the complete set, so a chip and its filtered rows always agree.
                count={inviteFilterCount(counts, filter.value)}
                selected={statusFilter === filter.value}
                onToggle={() => setStatusFilter(filter.value)}
                testId={`${PREFIX}-filter-${filter.label.toLowerCase()}`}
              />
            ))}
          </FilterBar>
        }
        emptyState={
          <EmptyState
            testId={`${PREFIX}-empty-state`}
            icon={<Icon icon={MailPlus} size={20} aria-hidden />}
            title={statusFilter === null ? 'No invites yet' : 'No invites with that status'}
            description={
              statusFilter === null
                ? 'Add work email addresses above to invite your colleagues, or send them the announcement below.'
                : 'Try another status filter.'
            }
          />
        }
      />

      <AnnouncementCard companyName={companyName} companyDomain={companyDomain} />
    </div>
  );
}

/** The enrollment-rate meter, or an action when no declared headcount makes a rate possible. */
function EnrollmentCard({
  enrollment,
  loading,
  isError,
  onRetry,
}: {
  enrollment: EnrollmentRateView | null;
  loading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  // Where the headcount is edited is this host's place (EP-MNT5(h)): Settings in the portal, the
  // Profile page in the console.
  const { words } = useEmployerModulePaths();
  return (
    <Card testId={`${PREFIX}-enrollment`}>
      <CardBody>
        <div className="flex flex-col gap-8">
          <span className="flex items-center gap-6 font-sans text-s13 font-w500 tracking-normal text-text-secondary">
            Enrollment rate
            <MethodologyHint subject="Enrollment rate" testId={`${PREFIX}-enrollment-methodology`}>
              {enrollmentMethodology(enrollment, words)}
            </MethodologyHint>
          </span>
          {loading ? (
            <div className="h-8 w-full animate-pulse rounded-full bg-neutral-200" />
          ) : isError ? (
            // A failed company read, NOT a missing headcount — do not send an admin who already
            // entered one back to Settings to enter it again.
            <InlineError message="Could not load your company details." onRetry={onRetry} />
          ) : enrollment === null ? (
            <p className="font-sans text-s14 leading-h15 tracking-normal text-text-secondary">
              {words.headcountPromptInvites}
            </p>
          ) : (
            <>
              <Meter
                value={enrollment.ratio}
                max={1}
                tone="brand"
                ariaLabel="Enrollment rate"
                valueLabel={enrollmentPercentLabel(enrollment)}
                valueText={`${enrollmentPercentLabel(enrollment)} — ${enrollment.active} of ${enrollment.declared} employees`}
                testId={`${PREFIX}-enrollment-meter`}
              />
              <p className="font-sans text-s13 tracking-normal text-text-tertiary">
                {enrollment.active} of the {enrollment.declared} employees you told us you have
              </p>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/** Bulk add-by-email, with the per-address result echo the bulk-gifting surface established. */
function AddInvitesCard({
  value,
  onChange,
  parsedCount,
  submitting,
  onSubmit,
  lastResult,
}: {
  value: string;
  onChange: (next: string) => void;
  parsedCount: number;
  submitting: boolean;
  onSubmit: () => void;
  lastResult: { alreadyInvited: string[]; alreadyEnrolled: string[] } | null;
}) {
  return (
    <Card testId={`${PREFIX}-add`}>
      <CardHeader
        title="Invite employees"
        subtitle="Paste work email addresses — separated by commas, spaces, or new lines."
      />
      <CardBody>
        <div className="flex flex-col gap-12">
          <Textarea
            value={value}
            onChange={(event) => onChange(event.target.value)}
            rows={3}
            placeholder="alex@example.com, sam@example.com"
            aria-label="Work email addresses to invite"
            testId={`${PREFIX}-emails`}
          />
          <div className="flex flex-wrap items-center justify-between gap-12">
            <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
              {parsedCount === 0
                ? 'Addresses already invited or enrolled are skipped, not duplicated.'
                : `${parsedCount} ${parsedCount === 1 ? 'address' : 'addresses'} ready`}
            </span>
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              disabled={parsedCount === 0 || submitting}
              loading={submitting}
              leadingIcon={<Icon icon={MailPlus} size={14} aria-hidden />}
              onClick={onSubmit}
              testId={`${PREFIX}-submit`}
            >
              Send invites
            </Button>
          </div>

          {lastResult !== null &&
            (lastResult.alreadyInvited.length > 0 || lastResult.alreadyEnrolled.length > 0) && (
              <div
                className="flex flex-col gap-6 rounded-lg border border-border-subtle bg-surface-sunken p-12"
                data-testid={`${PREFIX}-skipped`}
              >
                {lastResult.alreadyEnrolled.length > 0 && (
                  <SkippedRow
                    label="Already enrolled"
                    emails={lastResult.alreadyEnrolled}
                    testId={`${PREFIX}-skipped-enrolled`}
                  />
                )}
                {lastResult.alreadyInvited.length > 0 && (
                  <SkippedRow
                    label="Already invited"
                    emails={lastResult.alreadyInvited}
                    testId={`${PREFIX}-skipped-invited`}
                  />
                )}
              </div>
            )}
        </div>
      </CardBody>
    </Card>
  );
}

/** One skip bucket, listing the actual addresses — a count alone cannot be reconciled against a file. */
function SkippedRow({
  label,
  emails,
  testId,
}: {
  label: string;
  emails: string[];
  testId: string;
}) {
  return (
    <p
      className="font-sans text-s13 leading-h15 tracking-normal text-text-secondary"
      data-testid={testId}
    >
      <span className="font-w600 text-text-strong">{label}:</span> {emails.join(', ')}
    </p>
  );
}

/** The pre-written announcement (§97) — copy-to-clipboard, zero backend, no invented URL. */
function AnnouncementCard({
  companyName,
  companyDomain,
}: {
  companyName: string;
  companyDomain: string | null | undefined;
}) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(
    () =>
      announcementTemplate({
        companyName,
        domain: companyDomain,
        appStoreUrl: APP_STORE_URL,
        playStoreUrl: PLAY_STORE_URL,
      }),
    [companyName, companyDomain],
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toast.success('Announcement copied');
    } catch {
      // A clipboard denial is a real failure with a real remedy — say what it is. The text is on screen
      // and selectable, so the admin is not stuck.
      toast.error('Could not copy. Select the text and copy it manually.');
    }
  };

  return (
    <Card testId={`${PREFIX}-announcement`}>
      <CardHeader
        title="Announcement email"
        subtitle="Send this yourself — it explains how to join and how the match works."
        actions={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            leadingIcon={<Icon icon={copied ? Check : Copy} size={14} aria-hidden />}
            onClick={() => void copy()}
            testId={`${PREFIX}-announcement-copy`}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        }
      />
      <CardBody>
        <pre
          className="overflow-x-auto whitespace-pre-wrap break-words font-sans text-s13 leading-h15 tracking-normal text-text-secondary"
          data-testid={`${PREFIX}-announcement-text`}
        >
          {text}
        </pre>
      </CardBody>
    </Card>
  );
}
